import type { LazyQueryExecFunction } from '@apollo/client';
import type { BusinessAddressRef } from '@seek/hirer-business-address';
import type { PaymentMethodType } from '@seek/payments-widget';

import { saveTrackingData } from 'src/app/shared/dataAnalytics/storage';
import { log } from 'src/app/utils/logging';
import {
  BusinessAddressValidationError,
  DraftClosedError,
} from 'src/app/v2/constants/errors';
import { getJobIdFromCart } from 'src/app/v2/utils/getJobIdFromCart';
import type {
  DraftJobQuery,
  Exact,
  GetCartQuery,
  Scalars,
} from 'src/graphql/generated';
import type { CheckoutComponentCallbacks } from 'src/types/checkout';

export const onPostAdButtonClicked = async ({
  businessAddressRef,
  getDraft,
  getCart,
  jobId,
  paymentMedium,
  paymentMethod,
  isSavedCreditCard,
}: {
  businessAddressRef?: React.RefObject<BusinessAddressRef>;
  getDraft: LazyQueryExecFunction<
    DraftJobQuery,
    Exact<{
      id: Scalars['ID']['input'];
    }>
  >;
  getCart: LazyQueryExecFunction<
    GetCartQuery,
    Exact<{
      cartId: Scalars['ID']['input'];
    }>
  >;
  jobId?: string;
  paymentMethod?: PaymentMethodType;
  paymentMedium?: string;
  isSavedCreditCard?: boolean;
}): Promise<void> => {
  try {
    // JIRA ticket: https://myseek.atlassian.net/browse/JPU-3877
    // This check will be used for tracking the impact of job id mismatch
    // Should issue is widespread, consider messaging to prompt user to refresh the page
    // Get latest cart to cross check that the job has not been modified in other tabs
    const { data: { cart } = {} } = await getCart();
    const jobIdFromLatestCart = getJobIdFromCart(cart?.items);
    if (jobId !== jobIdFromLatestCart) {
      log.error('CheckoutSection | onPostAdButtonClick | jobId mismatch', {
        jobId,
        jobIdFromLatestCart,
        cart: JSON.stringify(cart),
      });
    }
  } catch (error) {
    // Fail silently because this is just a diagnostic check
    // Warn so no noise in production
    log.warn('CheckoutSection | onPostAdButtonClick | getCart error', {
      error,
      jobId,
    });
  }

  saveTrackingData({
    isPaidByStoredCreditCard:
      paymentMethod === 'CreditCard' && Boolean(isSavedCreditCard),
    paymentMediumSelected: paymentMedium || null,
  });

  if (!businessAddressRef) return;

  const validationResult =
    await businessAddressRef.current?.validateAllFields();

  if (validationResult) {
    throw new BusinessAddressValidationError(
      'Business address validation failed',
    );
  }

  await businessAddressRef.current?.submitFormAsync();
  const { data: { draftJob } = {} } = await getDraft();

  if (
    draftJob?.__typename === 'DraftJobNotFoundPayload' &&
    draftJob.reason === 'Not found'
  ) {
    throw new DraftClosedError('Draft is no longer open');
  }
};

export const onReplaceSavedCard: CheckoutComponentCallbacks['onReplaceSavedCard'] =
  () => saveTrackingData({ paymentMethod: 'CreditCard' });

export const onSaveCardOptionChanged: CheckoutComponentCallbacks['onSaveCardOptionChanged'] =
  (shouldSave) => saveTrackingData({ isSaveCreditCardChecked: shouldSave });

export const onSavedCardLoaded: CheckoutComponentCallbacks['onSavedCardLoaded'] =
  () => saveTrackingData({ paymentMethod: 'SavedCreditCard' });

export const onPaymentOptionChanged =
  (
    onChanged: (selectedItem: PaymentMethodType) => void,
  ): CheckoutComponentCallbacks['handlePaymentOptionChange'] =>
  (_selectedIndex, selectedItem) => {
    if (!selectedItem) {
      return;
    }

    const paymentMethod = selectedItem as PaymentMethodType;
    onChanged(paymentMethod);
    saveTrackingData({ paymentMethod });
  };
